<template>
  <nav class="navbar">
    <div class="nav-container">
      <!-- Brand Logo -->
      <div class="brand-section">
        <RouterLink to="/" class="brand-link">
          <div class="brand-logo">
            <span class="spark-icon">⚡</span>
            <span class="brand-text">KWIQ</span>
          </div>
          <div class="brand-text-container">
            <span class="brand-subtitle">PySpark Learning</span>
          </div>
        </RouterLink>
      </div>

      <!-- Navigation Menu -->
      <ul class="nav-menu">
        <li><RouterLink to="/" class="nav-link">Home</RouterLink></li>
        <li><RouterLink to="/about" class="nav-link">About</RouterLink></li>
        <li><RouterLink to="/samples" class="nav-link">Code Samples</RouterLink></li>
        <li><RouterLink to="/tools" class="nav-link">Tools</RouterLink></li>
      </ul>

      <!-- Cross-site Navigation -->
      <div class="site-switcher">
        <a href="https://kwiqsolutions.com" class="site-link">
          <i class="site-icon">🏢</i>
          <span class="site-text">Business</span>
        </a>
        <a href="https://boot.kwiqsolutions.com" class="site-link">
          <i class="site-icon">☕</i>
          <span class="site-text">Spring Boot</span>
        </a>
        <a href="https://express.kwiqsolutions.com" class="site-link">
          <i class="site-icon">⚡</i>
          <span class="site-text">Express.js</span>
        </a>
      </div>

      <!-- Mobile Menu Toggle -->
      <button class="mobile-menu-toggle" @click="toggleMobileMenu" aria-label="Toggle navigation">
        <span v-if="!isMobileMenuOpen">☰</span>
        <span v-if="isMobileMenuOpen">✕</span>
      </button>
    </div>

    <!-- Mobile Menu -->
    <div class="mobile-menu" :class="{ 'mobile-menu-open': isMobileMenuOpen }">
      <ul class="mobile-nav-menu">
        <li><RouterLink to="/" class="mobile-nav-link" @click="closeMobileMenu">Home</RouterLink></li>
        <li><RouterLink to="/about" class="mobile-nav-link" @click="closeMobileMenu">About</RouterLink></li>
        <li><RouterLink to="/samples" class="mobile-nav-link" @click="closeMobileMenu">Code Samples</RouterLink></li>
        <li><RouterLink to="/tools" class="mobile-nav-link" @click="closeMobileMenu">Tools</RouterLink></li>
        <li><a href="https://kwiqsolutions.com" class="mobile-nav-link">Business</a></li>
        <li><a href="https://boot.kwiqsolutions.com" class="mobile-nav-link">Spring Boot</a></li>
        <li><a href="https://express.kwiqsolutions.com" class="mobile-nav-link">Express.js</a></li>
      </ul>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink } from 'vue-router'

const isMobileMenuOpen = ref(false)

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}
</script>

<style scoped>
/* Navigation */
.navbar {
  background: var(--background);
  box-shadow: var(--shadow-md);
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 2px solid var(--primary-color);
}

.nav-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 70px;
}

/* Brand Section */
.brand-section {
  flex-shrink: 0;
}

.brand-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  gap: 0.75rem;
  transition: var(--transition);
}

.brand-link:hover {
  transform: translateY(-1px);
}

.brand-logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
}

.spark-icon {
  font-size: 1.75rem;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-color-dark));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.brand-text {
  color: var(--text-color);
}

.brand-text-container {
  display: flex;
  flex-direction: column;
}

.brand-subtitle {
  font-size: 0.8rem;
  color: var(--text-light);
  font-weight: 500;
  letter-spacing: 0.5px;
}

/* Navigation Menu */
.nav-menu {
  display: flex;
  list-style: none;
  gap: 1.5rem;
  margin: 0;
  padding: 0;
}

.nav-link {
  color: var(--text-color);
  font-weight: 500;
  text-decoration: none;
  transition: var(--transition);
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
  position: relative;
}

.nav-link:hover {
  color: var(--primary-color);
  background-color: rgba(225, 123, 71, 0.1);
  transform: translateY(-1px);
}

.router-link-active.nav-link {
  color: var(--primary-color);
  font-weight: 600;
  background-color: rgba(225, 123, 71, 0.15);
}

/* Site Switcher */
.site-switcher {
  display: flex;
  gap: 0.375rem;
  flex-shrink: 0;
  align-items: center;
}

.site-link {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--text-light);
  text-decoration: none;
  padding: 0.375rem 0.625rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 0.8rem;
  font-weight: 500;
  transition: var(--transition);
  white-space: nowrap;
  min-width: fit-content;
}

.site-link:hover {
  color: var(--primary-color);
  border-color: var(--primary-color);
  background-color: rgba(225, 123, 71, 0.05);
  transform: translateY(-1px);
}

.site-icon {
  font-size: 0.9rem;
  flex-shrink: 0;
}

.site-text {
  display: inline;
}

@media (max-width: 1200px) {
  .site-text {
    display: none;
  }
  
  .site-link {
    padding: 0.5rem;
    min-width: 2.25rem;
    justify-content: center;
  }
}

/* Mobile Menu Toggle */
.mobile-menu-toggle {
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-color);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: var(--radius);
  transition: var(--transition);
}

.mobile-menu-toggle:hover {
  background-color: var(--surface);
  color: var(--primary-color);
}

/* Mobile Menu */
.mobile-menu {
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--background);
  box-shadow: var(--shadow-lg);
  border-top: 1px solid var(--border);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s ease;
}

.mobile-menu-open {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.mobile-nav-menu {
  list-style: none;
  padding: 1rem 0;
  margin: 0;
}

.mobile-nav-link {
  display: block;
  color: var(--text-color);
  text-decoration: none;
  padding: 0.75rem 2rem;
  font-weight: 500;
  transition: var(--transition);
  border-left: 3px solid transparent;
}

.mobile-nav-link:hover,
.router-link-active.mobile-nav-link {
  background-color: rgba(225, 123, 71, 0.1);
  color: var(--primary-color);
  border-left-color: var(--primary-color);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .nav-container {
    padding: 1rem 1.5rem;
  }
  
  .nav-menu {
    gap: 1rem;
  }
  
  .site-switcher {
    gap: 0.25rem;
  }
  
  .site-link {
    font-size: 0.75rem;
    padding: 0.375rem 0.5rem;
  }
}

@media (max-width: 768px) {
  .nav-container {
    padding: 1rem 1.5rem;
    min-height: 60px;
  }
  
  .brand-logo {
    font-size: 1.25rem;
  }
  
  .spark-icon {
    font-size: 1.5rem;
  }
  
  .brand-subtitle {
    font-size: 0.7rem;
  }
  
  .nav-menu,
  .site-switcher {
    display: none;
  }
  
  .mobile-menu-toggle {
    display: block;
  }
  
  .mobile-menu {
    display: block;
  }
}
</style>